import {
  PROFILE_DOCUMENT_TYPE_LABELS,
  profileFormType,
  ProfileDocumentType,
  ProfileVerificationRow,
} from "../../../api/verificationsV2";
import { IColumnConfig } from "../../../components/ui/baseDataTable";
import SelectCell from "../../../components/ui/baseDataTable/cells/SelectCell";
import { ANOMALY_SELECT_OPTIONS } from "../../legalTechnicalAnalysis/components/anomalyOptions";
import FormConfigCell from "../../legalTechnicalAnalysis/components/FormConfigCell";


interface ProfileVerificationColumnsProps {
  rows: ProfileVerificationRow[];
  onRowChange: (
    rowIndex: number,
    value: any,
    field: keyof ProfileVerificationRow,
  ) => void;
}

const getAvailableDocumentTypes = (
  rows: ProfileVerificationRow[],
  currentRow: ProfileVerificationRow,
) => {
  const usedTypes = rows
    .filter((row) => row.verification_id !== currentRow.verification_id)
    .map((row) => row.document_type);

  return Object.values(ProfileDocumentType)
    .filter((documentType) => !usedTypes.includes(documentType))
    .map((documentType) => ({
      name: PROFILE_DOCUMENT_TYPE_LABELS[documentType],
      value: documentType,
    }));
};

export const buildProfileVerificationColumns = ({
  rows,
  onRowChange,
}: ProfileVerificationColumnsProps): IColumnConfig<ProfileVerificationRow>[] => [
  {
    headerName: "Document Type",
    field: "document_type",
    type: "custom",
    getValue: (row) => row.document_type ?? "",
    isEditable: (_, row) => row.isAdded,
    hideFocus: (row) => !row.isAdded,
    render: (rowIndex, value) => {
      const row = rows[rowIndex];

      return (
        <div className="min-w-[180px]">
          <SelectCell
            name="document type"
            value={value as string}
            isEditable={row.isAdded}
            options={getAvailableDocumentTypes(rows, row)}
            className="px-0"
            labelClassName="px-0 py-1 text-xs w-full"
            onChange={(updated) => onRowChange(rowIndex, updated, "document_type")}
          />
        </div>
      );
    },
  },
  {
    headerName: "Onboarding Status",
    field: "onboarding_details",
    type: "custom",
    getValue: (row) => row.onboarding_details,
    isEditable: true,
    render: (rowIndex, value) => {
      const row = rows[rowIndex];

      return (
        <FormConfigCell
          title="Onboarding Status"
          formType={
            row.document_type
              ? profileFormType(row.document_type)
              : undefined
          }
          disabled={!row.document_type}
          values={value ?? undefined}
          onChange={(updated) =>
            onRowChange(rowIndex, updated, "onboarding_details")
          }
        />
      );
    },
  },
  {
    headerName: "Current Status",
    field: "current_details",
    type: "custom",
    getValue: (row) => row.current_details,
    isEditable: true,
    render: (rowIndex, value) => {
      const row = rows[rowIndex];

      return (
        <FormConfigCell
          title="Current Status"
          formType={
            row.document_type
              ? profileFormType(row.document_type)
              : undefined
          }
          disabled={!row.document_type}
          values={value ?? undefined}
          onChange={(updated) =>
            onRowChange(rowIndex, updated, "current_details")
          }
        />
      );
    },
  },
  {
    headerName: "Anomaly",
    field: "anomaly",
    type: "select",
    getValue: (row) => row.anomaly ?? "",
    isEditable: (_, row) => Boolean(row.document_type),
    hideFocus: (row) => !row.document_type,
    onChange: onRowChange,
    options: ANOMALY_SELECT_OPTIONS,
    selectCellProps: {
      placeholder: "Anomaly",
      className: "px-0",
      rootContainerClassName: "min-w-[90px] flex items-center",
      labelClassName: "px-0 py-1 text-xs min-w-[90px]",
    },
  },
];
